import { useCallback } from 'react'
import * as Sentry from '@sentry/react'

export type TDriverRechargeOnboardingKeys = 'firstShow' | 'secondShow' | 'showed'
export type TWithdrawalSmzOnboardingKeys = 'firstShow' | 'secondShow' | 'showed'

export type StorageKey =
    | 'access-token'
    | 'driver-recharge-amount'
    | 'driver-recharge-onboarding'
    | 'driver-recharge-selected-method'
    | 'refresh-token'
    | 'user-cid'
    | 'user-id'
    | 'user-phone'
    | 'user-token'
    | 'withdrawal-funds-account-link'
    | 'withdrawal-smz-onboarding'
    | 'withdrawal-smz-popover-deducted'
    | 'withdrawal-smz-popover-upcoming'
    | 'withdrawal-smz-popover-withdrawal'

/** Приводит результат JSON.parse к ожидаемому примитиву по типу initialValue (рантайм ≠ дженерик). */
const coerceParsedToInitialPrimitive = <T>(parsed: unknown, initialValue: T): T => {
    if (typeof initialValue === 'string') {
        if (typeof parsed === 'string') {
            return parsed as T
        }

        if (parsed === null || typeof parsed === 'object') {
            return initialValue
        }

        return String(parsed) as T
    }

    if (typeof initialValue === 'number') {
        if (typeof parsed === 'number' && Number.isFinite(parsed)) {
            return parsed as T
        }

        if (typeof parsed === 'string') {
            const n = Number(parsed)

            return (Number.isFinite(n) ? n : initialValue) as T
        }

        return initialValue
    }

    if (typeof initialValue === 'object' && initialValue !== null) {
        if (typeof parsed === 'object' && parsed !== null && !Array.isArray(parsed)) {
            return parsed as T
        }

        return initialValue
    }

    return parsed as T
}

export const getLocalStorageValue = <T>(key: StorageKey, initialValue: T): T => {
    if (globalThis.window === undefined) {
        return initialValue
    }

    try {
        const item = globalThis.localStorage.getItem(key)

        if (!item) {
            return initialValue
        }

        try {
            const parsed: unknown = JSON.parse(item)

            return coerceParsedToInitialPrimitive(parsed, initialValue)
        } catch {
            if (typeof initialValue === 'string') {
                return item as T
            }

            return initialValue
        }
    } catch (error) {
        Sentry.captureException(error, {
            tags: {
                errorType: 'use-local-storage-get-value',
            },
        })

        return initialValue
    }
}

export const setLocalStorageValue = <T>(key: StorageKey, value: T): void => {
    try {
        if (globalThis.window !== undefined) {
            const stringValue = typeof value === 'string' ? value : JSON.stringify(value)

            globalThis.localStorage.setItem(key, stringValue)
        }
    } catch (error) {
        Sentry.captureException(error, {
            tags: {
                errorType: 'use-local-storage-set-value',
            },
        })
    }
}

export const useLocalStorage = <T>(
    key: StorageKey,
    initialValue: T,
): {
    getValue: () => T
    setValue: (value: T) => void
} => {
    const getValue = useCallback((): T => {
        return getLocalStorageValue(key, initialValue)
    }, [key, initialValue])

    const setValue = useCallback(
        (value: T): void => {
            setLocalStorageValue(key, value)
        },
        [key],
    )

    return {
        getValue,
        setValue,
    }
}
